// Chat/Room.tsx
import React, {useCallback, useContext, useEffect, useLayoutEffect, useRef, useState,} from 'react';
import {useParams} from 'react-router-dom';
import {axios} from 'Auth/axiosConfig';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import Divider from 'Core/components/elements/Divider';
import {headerHeight} from 'Core/components/Header/Header';
import {parseISO} from 'date-fns';
import {Message as ToastMessage} from 'Core/components/Message';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import {IMessage, IRoom} from 'Types/chat/models';
import {FC} from 'WideLayout/Layouts';
import pprint from 'Utils/pprint'; // <-- поправьте путь, если нужно
import MessageInput from 'Chat/MessageInput';
import {useRooms} from './RoomsContext';
import RoomHeader from './RoomHeader';
import MessagesContainer from './MessagesContainer';
import DateLabel from './DateLabel';

// ИСПОЛЬЗУЕМ наш обновлённый useWebSocket:
import useWebSocket from './useWebSocket';

interface RoomProps {
    room?: IRoom | null; // если хотим передать комнату напрямую
    roomId?: string;     // ID комнаты (обычно берем из URL)
    showHeader?: boolean;
}

const Room: React.FC<RoomProps> = ({
                                       room: roomProp,
                                       roomId: propRoomId,
                                       showHeader = true,
                                   }) => {
    const {roomId: routeRoomId} = useParams<{ roomId: string }>();
    const roomId = propRoomId ?? routeRoomId;

    const [messages, setMessages] = useState<IMessage[]>([]);
    const [room, setRoom] = useState<IRoom | null>(roomProp ?? null);
    const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);

    const {isAuthenticated, user} = useContext(AuthContext) as AuthContextType;
    const {byResponse, notAuthentication} = useErrorProcessing();
    const {updateRoom} = useRooms();

    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const isInitialLoad = useRef<boolean>(true);
    const prevScrollHeightRef = useRef<number>(0);
    const messagesRef = useRef<IMessage[]>(messages);
    const isFetchingRef = useRef<boolean>(false);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    /**
     * Фетчим сообщения (история) с бэка
     */
    const fetchMessages = useCallback(
        async (url: string | null = null) => {
            if (!roomId) return;
            // Если уже идёт запрос, прерываем
            if (isFetchingRef.current) return;

            isFetchingRef.current = true;
            setIsLoadingMore(true);

            try {
                // Запоминаем высоту до подгрузки (чтобы потом сохранить позицию при догрузке)
                if (url && messagesContainerRef.current) {
                    prevScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
                }

                const response = await axios.get(url || `/api/v1/rooms/${roomId}/messages/`);
                pprint('Messages fetched:', response.data);

                setMessages((prevMessages) => {
                    // Добавляем новые сверху (history) – не дублируем
                    const newMessages = response.data.results.filter(
                        (msg: IMessage) => !prevMessages.some((existing) => existing.id === msg.id)
                    );
                    return [...newMessages, ...prevMessages];
                });
                setNextPageUrl(response.data.next);

                // Если первая загрузка – скроллим в самый низ
                if (isInitialLoad.current && !url) {
                    setTimeout(() => {
                        messagesContainerRef.current?.scrollTo({
                            top: messagesContainerRef.current.scrollHeight,
                            behavior: 'auto',
                        });
                    }, 100);
                    isInitialLoad.current = false;
                }
                // Если догружаем историю (скроллим вверх)
                else if (url && messagesContainerRef.current) {
                    setTimeout(() => {
                        if (url && messagesContainerRef.current) {
                            const newScrollHeight = messagesContainerRef.current.scrollHeight;
                            messagesContainerRef.current.scrollTop =
                                newScrollHeight - prevScrollHeightRef.current;
                        }
                    }, 100);
                }
            } catch (error) {
                byResponse(error);
            } finally {
                setIsLoadingMore(false);
                isFetchingRef.current = false;
            }
        },
        [roomId, byResponse]
    );

    /**
     * Фетчим данные о комнате (если roomProp не передан напрямую)
     */
    const fetchRoom = useCallback(async () => {
        if (roomProp || !roomId) return;

        try {
            const response = await axios.get(`/api/v1/rooms/${roomId}/`);
            pprint('Room fetched:', response.data);
            setRoom(response.data);
        } catch (error) {
            byResponse(error);
        }
    }, [roomId, roomProp, byResponse]);

    /**
     * Подключаем WebSocket хук
     */
    const handleWebSocketMessage = useCallback(
        (data: any) => {
            if (data.action === 'confirm_message_saved') {
                // Пришло подтверждение, что сообщение сохранено на бэке
                setMessages((prev) => {
                    // Находим сообщение по tempId
                    const idx = prev.findIndex(
                        (msg) => msg.id === data.tempId || msg.tempId === data.tempId
                    );
                    if (idx !== -1) {
                        const newArr = [...prev];
                        newArr[idx] = {...data, status: 'sent'};
                        return newArr;
                    } else {
                        return [...prev, data];
                    }
                });
                if (data.room) {
                    updateRoom(data.room);
                }
            } else if (data.action === 'read_message') {
                // Пометка прочитанным
                setMessages((prev) => {
                    const idx = prev.findIndex((msg) => msg.id === data.id);
                    if (idx !== -1) {
                        const newArr = [...prev];
                        newArr[idx] = {...newArr[idx], is_read: true};
                        return newArr;
                    }
                    return prev;
                });
                if (data.room) {
                    updateRoom(data.room);
                }
            }
        },
        [updateRoom]
    );

    const sendMessage = useWebSocket({
        roomId: roomId || '',
        isAuthenticated: !!isAuthenticated,
        userId: user?.id ?? null,
        onMessage: handleWebSocketMessage,
    });

    /**
     * При заходе в комнату (или при смене roomId) – грузим сообщения, инфо о комнате
     */
    useEffect(() => {
        if (!isAuthenticated) {
            notAuthentication();
            return;
        }
        if (roomId) {
            fetchMessages(null);
            fetchRoom();
        }
    }, [roomId, isAuthenticated, notAuthentication, fetchMessages, fetchRoom]);

    /**
     * Сбрасываем сообщения/ссылку на "next" при смене комнаты
     */
    useEffect(() => {
        setMessages([]);
        setNextPageUrl(null);
        isInitialLoad.current = true;
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = 0;
        }
    }, [roomId]);

    /**
     * Отправка нового сообщения
     */
    const base64ToBlob = useCallback((base64: string, type: string): Blob => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], {type});
    }, []);

    const handleSendMessage = useCallback(
        async (messageText: string, attachedFiles: any[], isImportant: boolean) => {
            if (!user) {
                ToastMessage.error('Вы не авторизованы!');
                return;
            }
            if (!roomId) {
                ToastMessage.error('Нет roomId!');
                return;
            }

            const tempId = 'temp_' + new Date().toISOString();

            const messagePayload = {
                type: 'chat_message',
                room: roomId,
                message: messageText,
                files: attachedFiles,
                is_important: isImportant,
                tempId,
            };

            // "Временное" сообщение (до ответа от WebSocket)
            const tempMessage: IMessage = {
                id: null,
                tempId,
                room: Number(roomId),
                text: messageText,
                user: {
                    id: user.id,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    avatar: user.avatar,
                },
                files: attachedFiles.map((file) => {
                    const blob = base64ToBlob(file.data, file.type);
                    const url = URL.createObjectURL(blob);
                    return {
                        id: null,
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        file: url,
                    };
                }),
                created_at: new Date().toISOString(),
                is_read: false,
                is_important: isImportant,
                status: 'sending',
            };

            setMessages((prev) => [...prev, tempMessage]);

            try {
                if (sendMessage) {
                    sendMessage(JSON.stringify(messagePayload));
                } else {
                    ToastMessage.error('WebSocket не подключен.');
                }
            } catch (error) {
                byResponse(error);
            }
        },
        [roomId, user, byResponse, base64ToBlob, sendMessage]
    );

    const handleFileChange = useCallback((_updatedFiles: File[]) => {
        // Доп. логика при необходимости
    }, []);

    useLayoutEffect(() => {
        // После добавления нового сообщения – скролл вниз (если это не подгрузка истории)
        if (!isLoadingMore && !isInitialLoad.current) {
            messagesContainerRef.current?.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [messages, isLoadingMore]);

    // Чистим blob-URLы при размонтировании, чтобы не засорять память
    useEffect(() => {
        return () => {
            messages.forEach((message) => {
                message.files?.forEach((file: any) => {
                    if (file.file.startsWith('blob:')) {
                        URL.revokeObjectURL(file.file);
                    }
                });
            });
        };
    }, [messages]);

    /**
     * Отображаем заголовок даты между сообщениями
     */
    const renderDateLabel = useCallback(
        (message: IMessage, previousMessage?: IMessage) => {
            const currentDate = parseISO(message.created_at);
            if (!previousMessage) {
                return <DateLabel key={`date-${message.id}`} date={message.created_at}/>;
            }
            const prevDate = parseISO(previousMessage.created_at);
            if (currentDate.getDate() !== prevDate.getDate()) {
                return <DateLabel key={`date-${message.id}`} date={message.created_at}/>;
            }
            return null;
        },
        []
    );

    // Пометить сообщение как прочитанное
    const handleMarkAsRead = useCallback(
        (message: IMessage) => {
            if (user && user.id !== message.user.id && !message.is_read) {
                const payload = {type: 'read_message', message_id: message.id};
                if (sendMessage) {
                    sendMessage(JSON.stringify(payload));
                }
            }
        },
        [user, sendMessage]
    );

    // Помечаем все сообщения (кроме своих) прочитанными
    const handleMarkAsReadForAll = useCallback(() => {
        messagesRef.current.forEach((message) => {
            handleMarkAsRead(message);
        });
    }, [handleMarkAsRead]);

    useEffect(() => {
        if (isAuthenticated && room) {
            handleMarkAsReadForAll();
        }
    }, [messages, isAuthenticated, room, handleMarkAsReadForAll]);

    // Догрузка предыдущих сообщений (скроллим наверх -> догружаем)
    const handleScroll = useCallback(() => {
        if (
            messagesContainerRef.current?.scrollTop === 0 &&
            nextPageUrl &&
            !isLoadingMore &&
            !isFetchingRef.current
        ) {
            fetchMessages(nextPageUrl);
        }
    }, [fetchMessages, nextPageUrl, isLoadingMore]);

    useEffect(() => {
        const container = messagesContainerRef.current;
        container?.addEventListener('scroll', handleScroll);
        return () => {
            container?.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    // --- RENDER ---
    return (
        <FC h={'100%'} cls={'room'} maxH={`calc(100vh - ${headerHeight}px)`}>
            {/* Заголовок комнаты */}
            {showHeader && (
                <>
                    <RoomHeader room={room}/>
                    <Divider width={'90%'}/>
                </>
            )}

            {/* Контейнер сообщений */}
            <MessagesContainer
                messages={messages}
                room={room}
                renderDateLabel={renderDateLabel}
                isLoadingMore={isLoadingMore}
                messagesEndRef={messagesEndRef}
                messagesContainerRef={messagesContainerRef}
            />

            {/* Поле ввода сообщения */}
            <FC pb={1}>
                <FC rounded={3} pt={1} pb={1}>
                    <MessageInput onSend={handleSendMessage} onFileChange={handleFileChange}/>
                </FC>
            </FC>
        </FC>
    );
};

export default Room;
